import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Mic, Video, Newspaper, ArrowRight, FileText } from 'lucide-react';
import { PERSONAS } from '../lib/personas.js';

// Public news-engine API base. Override per env via Vite's VITE_NEWS_API_BASE.
// Defaults to a relative /api/media path so a Netlify proxy or same-origin
// deployment "just works"; for split-domain (Netlify frontend + Railway
// API) set VITE_NEWS_API_BASE=https://news-engine.up.railway.app/api/media
const NEWS_API_BASE = import.meta.env.VITE_NEWS_API_BASE || '/api/media';

// /media — Alfred's content hub. Multiple media types in one place so a
// reader can jump in wherever they want:
//   - Podcast (Track 2 from marketing_strategy_v1.2.md)
//   - News commentary segments (short, persona-hosted)
//   - Video segments / packages (long-form, B-roll voiceover, infotainment)
//   - More to come
//
// Phase 2 status: page shell with tabs + per-section "coming soon" cards.
// Each section wires up to its own API as those services come online.
//   News segments → news-engine /api/news/segments (Phase 1 days 2-5)
//   Podcast       → podcast feed / RSS (TBD service)
//   Video packages → news-engine extended for package format (TBD)
//
// Design intent (per docs/CONTENT_ENGINE/README.md §3.5): field-reporting
// style for short-form; package format (B-roll voiceover, infotainment)
// for long-form. See §3.6 for the package-format spec.

const SECTIONS = [
  { id: 'all',      label: 'All',           icon: null },
  { id: 'video',    label: 'Video Segments', icon: Video },
  { id: 'news',     label: 'News',          icon: Newspaper },
  { id: 'podcast',  label: 'Podcast',       icon: Mic }
];

export default function Media() {
  const [active, setActive] = useState('all');

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 py-12 md:py-20">
        <p className="text-xs uppercase tracking-[0.3em] text-alfred-gold">
          Alfred Media
        </p>
        <h1 className="mt-4 text-4xl md:text-6xl font-serif leading-tight">
          Men's health, told well.
        </h1>
        <p className="mt-5 text-lg text-stone-300 max-w-2xl">
          Podcasts, video segments, news commentary — Allen, Collin, and Jose
          read what physicians are reading and tell you what's worth knowing.
          Pick a format, jump in.
        </p>
        <p className="mt-3 text-sm text-stone-500 max-w-2xl">
          AI-narrated editorial commentary. Not medical advice. Sources cited
          on every segment. Talk to your doctor about what affects you.
        </p>
      </section>

      {/* ── Section tabs ──────────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 pb-6">
        <div className="flex flex-wrap gap-2">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                className={`px-4 py-2 rounded-full border text-sm transition flex items-center gap-2 ${
                  isActive
                    ? 'border-alfred-gold text-alfred-gold bg-alfred-gold/10'
                    : 'border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-200'
                }`}
              >
                {Icon && <Icon size={14} />}
                {s.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Sections ──────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 pb-16 space-y-12">
        {(active === 'all' || active === 'video') && <VideoSection />}
        {(active === 'all' || active === 'news') && <NewsSection />}
        {(active === 'all' || active === 'podcast') && <PodcastSection />}
      </section>

      {/* ── Soft CTA back to product ──────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 pb-20">
        <div className="rounded-2xl border border-stone-900 bg-stone-950/60 px-6 py-8">
          <p className="text-xs uppercase tracking-[0.25em] text-alfred-gold">
            Want to talk to one of them?
          </p>
          <h2 className="mt-3 text-2xl md:text-3xl font-serif leading-tight">
            Pick a voice. Alfred calls back in 60 seconds.
          </h2>
          <p className="mt-3 text-stone-400 max-w-xl">
            The same Allen, Collin, and Jose who break down the news will
            pick up the phone and walk you through anything you watched.
          </p>
          <Link
            to="/start"
            className="mt-5 inline-flex items-center gap-2 text-alfred-gold hover:text-amber-400 transition"
          >
            Pick a voice <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}

// ── Video Segments (long-form package format — coming soon) ──────────
function VideoSection() {
  return (
    <div>
      <SectionHeader
        icon={Video}
        title="Video Segments"
        subtitle="Long-form packages — voiceover, B-roll, animation. 2-4 min."
      />
      <div className="rounded-2xl border border-stone-900 bg-stone-950/60 p-8 text-center">
        <Video size={28} className="mx-auto text-stone-500 mb-3" />
        <p className="text-stone-300">Coming Soon.</p>
        <p className="mt-2 text-sm text-stone-500">
          Long-form video segments land here when the first package ships.
        </p>
      </div>
    </div>
  );
}

// ── News (1-minute read) ─────────────────────────────────────────────
// Fetches the latest published digest from news-engine /api/media/digest —
// an easy-English roundup of the top auto-approved weight-loss stories,
// with a "why it matters to you" for each, plus links to the sources.
// Falls back to per-persona coming-soon cards when no digest exists yet.
function NewsSection() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${NEWS_API_BASE}/digest`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`http ${r.status}`)))
      .then((j) => { if (!cancelled) setDigest(j.data || null); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const fallback = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {PERSONAS.map((p) => (
        <ComingSoonCard
          key={p.id}
          persona={p}
          kind="news"
          blurb={`First takes from ${p.name.split(' ')[0]} drop soon — what's worth your time, what isn't.`}
        />
      ))}
    </div>
  );

  return (
    <div>
      <SectionHeader
        icon={Newspaper}
        title="News"
        subtitle="Your 1-minute read — what's new in weight-loss health, in plain English."
      />
      {loading ? (
        <div className="text-stone-500 text-sm py-4">Loading…</div>
      ) : failed || !digest ? (
        fallback
      ) : (
        <DigestCard digest={digest} />
      )}
    </div>
  );
}

// Reads: { title, intro, read_minutes, stories: [{ headline, why, source,
// source_url }], outro, published_at }.
function DigestCard({ digest }) {
  const stories = Array.isArray(digest.stories) ? digest.stories : [];
  const mins = Math.max(1, Math.round(Number(digest.read_minutes) || 1));
  const dateStr = digest.published_at
    ? new Date(digest.published_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <article className="rounded-2xl border border-stone-900 bg-stone-950/60 p-6 md:p-8">
      <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-alfred-gold">
        <span className="inline-flex items-center gap-1">
          <FileText size={12} /> {mins} min read
        </span>
        {dateStr && <span className="text-stone-500">· {dateStr}</span>}
      </div>

      <h3 className="mt-3 text-2xl md:text-3xl font-serif leading-tight">{digest.title}</h3>
      {digest.intro && <p className="mt-3 text-stone-300 leading-relaxed">{digest.intro}</p>}

      <ol className="mt-7 space-y-6">
        {stories.map((s, i) => (
          <li key={i} className="border-l-2 border-stone-800 pl-4">
            <div className="flex items-start gap-3">
              <span className="font-serif text-2xl text-alfred-gold leading-none">{i + 1}</span>
              <div className="flex-1">
                <h4 className="font-serif text-lg leading-snug">{s.headline}</h4>
                <p className="mt-2 text-sm text-stone-300 leading-relaxed">{s.why}</p>
                {s.source_url && (
                  <a
                    href={s.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-alfred-gold hover:text-amber-400 transition"
                  >
                    Read the source{s.source ? ` · ${s.source}` : ''} <ArrowRight size={12} />
                  </a>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {digest.outro && (
        <p className="mt-7 border-t border-stone-900 pt-5 text-sm text-stone-400 leading-relaxed">
          {digest.outro}
        </p>
      )}
    </article>
  );
}

// ── Podcast (call-in show) ───────────────────────────────────────────
// Plays the latest generated episode from the static metadata file written
// by news-engine/src/media/podcast.js. Falls back to the coming-soon panel
// when no episode has been published yet.
function PodcastSection() {
  const [episodes, setEpisodes] = useState([]);      // index listing (newest first)
  const [selectedEp, setSelectedEp] = useState(null); // episode number, e.g. "02"
  const [current, setCurrent] = useState(null);       // full episode json (w/ transcript)
  const [loading, setLoading] = useState(true);

  // Load the back-catalog index. Fall back to a bare episode-01 if no index yet.
  useEffect(() => {
    let cancelled = false;
    fetch('/media/podcast/index.json')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('no index')))
      .then((j) => {
        const eps = Array.isArray(j.episodes) ? j.episodes : [];
        if (cancelled) return;
        if (eps.length) { setEpisodes(eps); setSelectedEp(eps[0].episode); }
        else setLoading(false);
      })
      .catch(() => {
        // Backward-compat: no index — try episode-01 directly.
        fetch('/media/podcast/episode-01.json')
          .then((r) => r.ok ? r.json() : Promise.reject(new Error('none')))
          .then((j) => {
            if (cancelled) return;
            if (j?.audio_url) {
              setEpisodes([{ episode: j.episode || '01', title: j.title, duration_seconds: j.duration_seconds }]);
              setSelectedEp(j.episode || '01');
            } else setLoading(false);
          })
          .catch(() => { if (!cancelled) setLoading(false); });
      });
    return () => { cancelled = true; };
  }, []);

  // Load the selected episode's full payload (transcript + cast) on demand.
  useEffect(() => {
    if (!selectedEp) return;
    let cancelled = false;
    setLoading(true);
    fetch(`/media/podcast/episode-${selectedEp}.json`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('missing')))
      .then((j) => { if (!cancelled) setCurrent(j); })
      .catch(() => { if (!cancelled) setCurrent(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedEp]);

  const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '');

  return (
    <div>
      <SectionHeader
        icon={Mic}
        title="Podcast"
        subtitle="Call-in show — our AI hosts take listener questions on weight-loss health."
      />
      {loading && !current ? (
        <div className="text-stone-500 text-sm py-4">Loading…</div>
      ) : current ? (
        <>
          <EpisodeCard episode={current} />
          {episodes.length > 1 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">All episodes</p>
              <div className="space-y-2">
                {episodes.map((ep) => {
                  const active = String(ep.episode) === String(selectedEp);
                  return (
                    <button
                      key={ep.episode}
                      type="button"
                      onClick={() => setSelectedEp(ep.episode)}
                      className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                        active
                          ? 'border-alfred-gold bg-alfred-gold/10'
                          : 'border-stone-900 bg-stone-950/60 hover:border-stone-700'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-stone-200 truncate">
                          Ep {ep.episode} — {ep.title}
                        </span>
                        <span className="text-xs text-stone-500 shrink-0">{fmtDur(ep.duration_seconds)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-2xl border border-stone-900 bg-stone-950/60 p-8 text-center">
          <Mic size={28} className="mx-auto text-stone-500 mb-3" />
          <p className="text-stone-300">Coming Soon.</p>
          <p className="mt-2 text-sm text-stone-500">Episode 1 lands here shortly.</p>
        </div>
      )}
    </div>
  );
}

// Reads: { title, description, audio_url, duration_seconds, cast: { hosts,
// callers }, transcript: [{ speaker, role, text }] }.
function EpisodeCard({ episode }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const dur = Number(episode.duration_seconds) || 0;
  const durStr = dur ? `${Math.floor(dur / 60)}:${String(Math.round(dur % 60)).padStart(2, '0')}` : null;
  const hosts = episode.cast?.hosts || [];
  const callers = episode.cast?.callers || [];

  return (
    <article className="rounded-2xl border border-stone-900 bg-stone-950/60 p-6 md:p-8">
      <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-alfred-gold">
        <span className="inline-flex items-center gap-1"><Mic size={12} /> Episode {episode.episode || '01'}</span>
        {durStr && <span className="text-stone-500">· {durStr}</span>}
      </div>

      <h3 className="mt-3 text-2xl md:text-3xl font-serif leading-tight">{episode.title}</h3>
      {episode.description && <p className="mt-3 text-stone-300 leading-relaxed">{episode.description}</p>}

      {(hosts.length > 0 || callers.length > 0) && (
        <p className="mt-3 text-xs text-stone-500">
          {hosts.length > 0 && <>Hosts: {hosts.join(', ')}</>}
          {callers.length > 0 && <> · Callers: {callers.join(', ')}</>}
        </p>
      )}

      <audio controls preload="none" className="mt-5 w-full" src={episode.audio_url}>
        Your browser does not support audio playback.
      </audio>

      {Array.isArray(episode.transcript) && episode.transcript.length > 0 && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setShowTranscript((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-alfred-gold hover:text-amber-400 transition"
          >
            <FileText size={12} /> {showTranscript ? 'Hide transcript' : 'Show transcript'}
          </button>
          {showTranscript && (
            <div className="mt-4 space-y-3 border-t border-stone-900 pt-4">
              {episode.transcript.map((line, i) => (
                <p key={i} className="text-sm leading-relaxed">
                  <span className={`font-medium ${line.role === 'caller' ? 'text-stone-400' : 'text-alfred-gold'}`}>
                    {line.speaker}:
                  </span>{' '}
                  <span className="text-stone-300">{line.text}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ── Reusable bits ─────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="mt-1 text-alfred-gold">{Icon && <Icon size={20} />}</div>
      <div>
        <h2 className="text-xl md:text-2xl font-serif">{title}</h2>
        <p className="mt-1 text-sm text-stone-400">{subtitle}</p>
      </div>
    </div>
  );
}

function ComingSoonCard({ persona, kind, blurb }) {
  const Icon = persona.icon;
  return (
    <article
      className={`relative overflow-hidden rounded-2xl border border-stone-900 bg-gradient-to-br ${persona.accent} p-6 min-h-[240px] flex flex-col`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} className="text-stone-300" />
        <p className="text-xs uppercase tracking-[0.2em] text-stone-400">
          {persona.name}
        </p>
      </div>
      <h3 className="mt-3 text-lg font-serif">{persona.tagline}</h3>
      <p className="mt-3 text-sm text-stone-300 flex-1">{blurb}</p>
      <div className="mt-4 flex items-center gap-2 text-xs text-stone-500">
        <Play size={12} />
        <span>Coming soon</span>
      </div>
    </article>
  );
}

// Real <SegmentCard> — lit up by NewsSection when the API returns data.
// Reads: { id, persona, persona_name, title, source, source_url,
// published_at, duration_seconds, youtube_video_id, on_screen_title }.
function SegmentCard({ segment }) {
  const personaMeta = PERSONAS.find((p) => p.id === segment.persona);
  return (
    <Link
      to={`/media/${segment.id}`}
      className="group relative overflow-hidden rounded-2xl border border-stone-900 bg-stone-950 hover:border-stone-800 transition flex flex-col"
    >
      <div className="aspect-video bg-stone-900 relative">
        {segment.youtube_video_id ? (
          <img
            src={`https://i.ytimg.com/vi/${segment.youtube_video_id}/mqdefault.jpg`}
            alt=""
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-700">
            <Play size={36} />
          </div>
        )}
        <div className="absolute bottom-2 right-2 rounded bg-stone-950/80 px-2 py-1 text-[10px] uppercase tracking-wider text-stone-300">
          {Math.round(segment.duration_seconds || 30)}s
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] uppercase tracking-[0.2em] text-alfred-gold">
          {personaMeta?.name || segment.persona_name || segment.persona}
        </p>
        <h3 className="mt-2 text-base font-serif leading-snug flex-1">
          {segment.on_screen_title || segment.title}
        </h3>
        {segment.source && (
          <p className="mt-3 text-xs text-stone-500 flex items-center gap-1">
            <FileText size={11} />
            via {segment.source}
          </p>
        )}
      </div>
    </Link>
  );
}
